import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BackAndMultipleButtons,
  SaveButton,
  SaveTempButton,
} from 'components/base/BottomStepComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import PublishCampaignPrompt from 'components/campaign/campaignCreation/PublishCampaignPrompt';
import RejectPrompt from 'components/campaign/campaignCreation/RejectPrompt';
import {
  APIStatus,
  ActionType,
  ApprovalStatus,
  CampaignType,
  STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY,
  STAMP_CAMPAIGN_PREVIEW_PAGE_ANCHOR_KEY,
  STAMP_CAMPAIGN_REWARDS_PAGE_ANCHOR_KEY,
  STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';

import { createAction } from 'utils';

import '../../campaign/campaignCreation/CreateCampaignStepFour.scss';
import BaseCreationStepContentByConfig from 'components/base/BaseCreationStepContentByConfig';
import { CREATE_STAMP_CAMPAIGN_PREVIEW_PAGE_CONFIG } from './StampCampaignCreationConfig';
import RecurringMessageSendToAllAgainPrompt from 'components/campaign/campaignCreation/RecurringMessageSendToAllAgainPrompt';
import BaseAnchor from 'components/base/BaseAnchor';
import { validate } from 'containers/engagement/stampCampaign/CreateStampCampaignValidata';
import { ProcessEnvConfig } from 'config/ProcessEnvConfig';

export const stampCampaignAnchorConfig = [
  {
    title: "Campaign content",
    anchorKey: STAMP_CAMPAIGN_PREVIEW_PAGE_ANCHOR_KEY.campaignContent.sectionKey,
    className: "",
    sublist: [
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionKey,
        title: "General"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionKey,
        title: "Target customers"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionKey,
        title: "Key visual"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionKey,
        title: "Instruction"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.detail.sectionKey,
        title: "Detail"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionKey,
        title: "Front end display"
      },
      ...(
        ProcessEnvConfig.haveShopMall
          ? [{ anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.mall.sectionKey, title: 'Mall' }]
          : []
      ),
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionKey,
        title: "Active period"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.visiblePeriod.sectionKey,
        title: "Visible period"
      },
      {
        anchorKey: STAMP_CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaign.sectionKey,
        title: "Recurring Campaign"
      },
    ]
  },
  {
    title: "Rule & Properties",
    anchorKey: STAMP_CAMPAIGN_PREVIEW_PAGE_ANCHOR_KEY.properties.sectionKey,
    className: "",
    sublist: [
      {
        anchorKey: STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY.tasks.sectionKey,
        title: "Task"
      },
    ]
  },
  {
    title: "Rewards",
    anchorKey: STAMP_CAMPAIGN_PREVIEW_PAGE_ANCHOR_KEY.rewards.sectionKey,
    className: "",
    sublist: [
      {
        anchorKey: STAMP_CAMPAIGN_REWARDS_PAGE_ANCHOR_KEY.rewards.sectionKey,
        title: "Reward"
      },
      {
        anchorKey: STAMP_CAMPAIGN_REWARDS_PAGE_ANCHOR_KEY.executionPriority.sectionKey,
        title: "Execution priority for global limit"
      },
    ]
  }
];

const mapPropsToState = (state) => ({
  campaignId: state.createCampaign.campaign?.pk,
  createStatus: state.createCampaign.createStatus,
  formHasSubmitted: state.createCampaign.formHasSubmitted,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  errorSectionList: state.createCampaign.errorSectionList,
});

function CreateStampCampaignStepFourV2({
  campaignId,
  createStatus,
  isSuperuser,
  userPermissions,
  formHasSubmitted,
  onStepChange,
  onSaveTemp,
  errorSectionList,
}) {
  const params = useParams();
  const isEditMode = !!params.id
  const formContext = useFormContext();
  const { getValues, setValue, clearErrors, setError, formState: { errors } } = formContext;
  const location = useLocation();
  const [showPublishPrompt, setShowPublishPrompt] = useState(false);
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [showSendToAllPrompt, setShowSendToAllPrompt] = useState(false);
  const [actionType, setActionType] = useState(ActionType.none);
  const approvalStatus = getValues('approvalStatus');
  const isBirthdayRecurringCampaign = getValues('isBirthdayRecurringCampaign');

  const dispatch = useDispatch();
  const history = useHistory();
  const hasPermission = (requires) => {
    if (isSuperuser) return true;
    if (userPermissions.includes(requires) || !requires) return true;

    return false;
  };

  useEffect(() => {
    if (createStatus === APIStatus.success && formHasSubmitted) {
      setShowPublishPrompt(
        actionType !== ActionType.approval ||
          history.location.pathname.includes('edit'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStatus, formHasSubmitted]);

  const backToLastStep = () => onStepChange(2);
  const publishCampaign = (buttonAction) => {
    if (
      buttonAction === ActionType.publish ||
      buttonAction === ActionType.forcePublish ||
      buttonAction === ActionType.approval
    ) {
      dispatch(
        createAction(
          isEditMode
            ? 'createCampaign/publishCampaignV2'
            : 'createCampaign/prePublishCampaign'
        )({
          data: getValues(),
          resume: location.state?.resume,
        }),
      );  
      return;
    }
    dispatch(
      createAction('createCampaign/createOrUpdateCampaignV2')({
        data: getValues(),
        actionType: buttonAction,
        resume: location.state?.resume,
        isStamp: true,
      }),
    );
  }
  
  const onClickPrimaryBtn = (buttonAction) => {
    const skipValidate = [
      ActionType.update,
      ActionType.save,
      ActionType.tempForPulished,
    ].includes(buttonAction);
    
    if (skipValidate) {
      setActionType(buttonAction);
      onSaveTemp({ ...formContext, buttonAction })
      return;
    }
    let isValid = true
    isValid = validate({
      dispatch,
      getValues: getValues,
      setError,
      clearErrors,
      checkCheckList: true,
    });

    if (!isValid) {
      return
    }
    setActionType(buttonAction);
    if (
      buttonAction === ActionType.withdraw ||
      buttonAction === ActionType.unpublish
    ) {
      dispatch(
        createAction('createCampaign/updateApprovalStatus')({
          data: getValues(),
          actionType: buttonAction,
          deleteBanner: buttonAction === ActionType.unpublish,
        }),
      );
      return 
    } 

    // show recurring message send to all prompt
    const recurringMessageChannels = getValues('recurringMessage.channels');
    if (
      isBirthdayRecurringCampaign &&
      [
        ActionType.forcePublish,
        ActionType.approval
      ].includes(buttonAction) &&
      recurringMessageChannels?.length > 0
    ) {
      setShowSendToAllPrompt(true);
      return;
    }
    publishCampaign(buttonAction)
  }

  const hasPublishPermisson = hasPermission(PermissionCodes.publishPendingCampaign);
  const showForceSaveButton = hasPublishPermisson && ![ApprovalStatus.pending, ApprovalStatus.published].includes(approvalStatus);
  const approvalActionMap = {
    [ApprovalStatus.published]: {
      text: 'Unpublish',
      actionType: ActionType.unpublish,
      requirePermission: hasPermission(PermissionCodes.withdrawPublishCampaignToPending)
        ? PermissionCodes.withdrawPublishCampaignToPending
        : PermissionCodes.withdrawPublishCampaignToDraft
    },
    [ApprovalStatus.pending]: hasPublishPermisson ? {
      text: 'Approve',
      actionType: ActionType.approval,
      requirePermission: PermissionCodes.publishPendingCampaign,
    } : {
      text: 'Pending for approval',
      actionType: ActionType.pendingForApproval,
      requirePermission: PermissionCodes.withdrawPendingCampaignToDraft,
    },
    default: {
      text: 'Submit for approval',
      actionType: ActionType.submitForApproval,
      requirePermission: PermissionCodes.pendingCampaign,
    },
  }

  const [approvalText, approvalActionType, saveRequires] = useMemo(() => {
    const approvalActionConfig = approvalActionMap?.[approvalStatus] || approvalActionMap?.default

    return [approvalActionConfig?.text, approvalActionConfig?.actionType, approvalActionConfig?.requirePermission]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, hasPublishPermisson, approvalActionMap])

  const tempSaveButtonMap = {
    [ApprovalStatus.draft]: {
      text: 'Update',
      actionType: ActionType.update,
      requirePermission: PermissionCodes.editDraftCampaign,
    },
    [ApprovalStatus.pending]: hasPublishPermisson ? {
      text: 'Update',
      actionType: ActionType.update,
      requirePermission: PermissionCodes.publishPendingCampaign,
    } : {
        text: 'Withdraw',
        actionType: ActionType.withdraw,
        requirePermission: PermissionCodes.withdrawPendingCampaignToDraft,
    },
    [ApprovalStatus.published]: {
      text: 'Update',
      actionType: ActionType.tempForPulished,
      requirePermission: PermissionCodes.tempForPublishCampaign
    },
    default: {
      text: 'Save as a draft',
      actionType: ActionType.save,
      requirePermission: PermissionCodes.createDraftCampaign,
    },
  }

  const [tempButtonText, tempActionType, tempRequires] = useMemo(() => {
    return [
      tempSaveButtonMap?.[approvalStatus]?.text || tempSaveButtonMap?.default?.text,
      tempSaveButtonMap?.[approvalStatus]?.actionType || tempSaveButtonMap?.default?.actionType,
      tempSaveButtonMap?.[approvalStatus]?.requirePermission || tempSaveButtonMap?.default?.requirePermission,
    ]
  }, [approvalStatus, tempSaveButtonMap]);


  const [extraButtons, extraPopContent] = useMemo(() => {
    const buttonList = [];
    const extraPopContentList = []
    if (hasPermission(tempRequires)) {
      buttonList.push(
        <SaveTempButton
          text={tempButtonText}
          action={() => {
            onClickPrimaryBtn(tempActionType);
          }}
        />,
      );
      extraPopContentList.push(
        {
          content: tempButtonText,
          action: () => onClickPrimaryBtn(tempActionType),
        },
      )
    }
    if (
      approvalStatus === ApprovalStatus.pending &&
      hasPermission(PermissionCodes.publishPendingMessage)
    ) {
      buttonList.push(
        <SaveTempButton
          text="Reject"
          action={() => {
            setShowRejectPrompt(true);
          }}
        />,
      );
      extraPopContentList.push(
        {
          content: "Reject",
          action: () => setShowRejectPrompt(true),
        },
      )
    }
    if (showForceSaveButton && hasPermission(saveRequires)) {
      buttonList.push(
        <SaveTempButton
          text={approvalText}
          action={() => approvalButtonAction()}
          disabled={
            approvalActionType === ActionType.pendingForApproval ? true : false
          }
        />,
      );
      approvalActionType !== ActionType.pendingForApproval && extraPopContentList.push(
        {
          content: approvalText,
          action: () => approvalButtonAction(),
        },
      )
    }
    return [buttonList, extraPopContentList];
  }, [approvalStatus]);

  const primaryButton = useMemo(() => {
    if (showForceSaveButton) {
      return (
        <SaveButton
          text={'Skip approval and Publish'}
          action={() => forcePublishButtonAction()}
        />
      );
    }
    if (hasPermission(saveRequires)) {
      return (
        <SaveButton
          text={approvalText}
          action={() => approvalButtonAction()}
          disabled={approvalActionType === ActionType.pendingForApproval}
        />
      );
    }
    return null;
  }, [approvalStatus]);

  const approvalButtonAction = () => {
    onClickPrimaryBtn(approvalActionType);
  };

  const forcePublishButtonAction = () => {
    onClickPrimaryBtn(ActionType.forcePublish);
  };

  return (
    <div className='create-campaign-preview-page'>
      <div className='create-campaign-preview-page-with-anchor'>
        <BaseCreationStepContentByConfig
          configuration={CREATE_STAMP_CAMPAIGN_PREVIEW_PAGE_CONFIG}
          hidePreview={true}
        />
        <BaseAnchor
          displayRefreshBtn
          onClickRefreshBtn={() => {
            validate({
              dispatch,
              getValues: getValues,
              setError,
              clearErrors,
              checkCheckList: true,
            });
          }}
          anchorListConfig= {stampCampaignAnchorConfig}
          errorSectionList={errorSectionList}
        />
      </div>
      <BackAndMultipleButtons
        backAction={backToLastStep}
        multipleButtons={[
          <ListButtonsGroup
            hideExtraButtonWidth={600}
            hideAllButtonWidth={400}
            hideExtraPopPosition="top"
            hideAllPopPosition="topRight"
            extraButtons={extraButtons}
            extraPopContent={extraPopContent}
            primaryButton={primaryButton}
            primaryPopContent={{
              requires: saveRequires,
              action: () => approvalButtonAction(),
              content: approvalText,
            }}
          />,
        ]}
      />

      <RejectPrompt
        show={showRejectPrompt}
        onHide={() => setShowRejectPrompt(false)}
        onConfirm={(comment) => {
          setShowRejectPrompt(false);
          dispatch(
            createAction('createCampaign/rejectCampaign')({
              id: params.id,
              message: comment,
              afterAction: () => {
                const pathname = location.pathname.split('/')[1];
                history.push({
                  pathname: `/${pathname}`,
                });
              },
            }),
          );
        }}
      />

      <RecurringMessageSendToAllAgainPrompt
        show={showSendToAllPrompt}
        onHide={() => setShowSendToAllPrompt(false)}
        onConfirm={(sendToAllAgain) => {
          setShowSendToAllPrompt(false);
          setValue(`recurringMessage.sendToAllAgain`, sendToAllAgain, { shouldDirty: true })
          publishCampaign(actionType)
        }}
      />

      <PublishCampaignPrompt
        campaignId={params.id || campaignId}
        campaignType={CampaignType.stampCampaign}
        actionType={actionType}
        show={showPublishPrompt}
        path={'/stamp_campaigns'}
      />
    </div>
  );
}


export default connect(mapPropsToState)(CreateStampCampaignStepFourV2);
